import { ParseTreeWalker } from "antlr4ng";
import { createParser } from "shared/antlr/frendsExpressionFactory";
import { FrendsExpressionParserListener } from "shared/antlr/FrendsExpressionParserListener";
import { EnvRefContext, SourceRefContext } from "shared/antlr/FrendsExpressionParser";

export type VdmExpressionMode = "text" | "csharp";

const SOURCE_KEYWORD = "#source";
const ENV_KEYWORD = "#env";

function sourcePathSuffix(path: string): string {
    if (!path) {
        return "";
    }

    return `.${path}`;
}

function isIdentifierChar(char: string): boolean {
    return /[a-z0-9_åäö]/i.test(char);
}

function parseSourceRef(expression: string, tokenStart: number): Pick<SourceRef, "path" | "pathEnd" | "tokenEnd"> {
    let index = tokenStart + SOURCE_KEYWORD.length;
    let pathEnd = index;
    let tokenEnd = index;

    while (index < expression.length) {
        const currentChar = expression[index];

        if (currentChar === "[") {
            const closingBracket = expression.indexOf("]", index + 1);
            if (closingBracket === -1) {
                break;
            }

            index = closingBracket + 1;
            pathEnd = index;
            tokenEnd = index;
            continue;
        }

        if (currentChar !== ".") {
            break;
        }

        let segmentEnd = index + 1;
        while (segmentEnd < expression.length && isIdentifierChar(expression[segmentEnd])) {
            segmentEnd++;
        }

        if (segmentEnd === index + 1) {
            break;
        }

        tokenEnd = segmentEnd;
        if (expression[segmentEnd] === "(") {
            break;
        }

        index = segmentEnd;
        pathEnd = segmentEnd;
    }

    const rawPath = expression.substring(tokenStart + SOURCE_KEYWORD.length, pathEnd);
    return {
        path: rawPath.replace(/^\./, ""),
        pathEnd,
        tokenEnd,
    };
}

export function formatSourceToken(path: string, mode: VdmExpressionMode): string {
    const suffix = sourcePathSuffix(path);
    if (mode === "csharp") {
        return `${SOURCE_KEYWORD}${suffix}`;
    }
    return `{{${SOURCE_KEYWORD}${suffix}}}`;
}

export interface SourceRef {
    path: string;
    start: number;
    // End position (exclusive) of the path portion within the full token
    pathEnd: number;
    // End position (exclusive) of the full ANTLR token (before method-call trimming)
    tokenEnd: number;
}

// Collects SOURCE_REF tokens from a parsed expression, trimming trailing method-call segments.
// E.g. #source.Name.ToString() → path "Name", pathEnd at end of "Name".
export function collectSourceRefs(expression: string, mode: VdmExpressionMode): SourceRef[] {
    const language = mode === "csharp" ? "csharp" : "handlebars";
    const parser = createParser(expression, language);
    const tree = mode === "csharp"
        ? parser.csExpressionRoot()
        : parser.textExpressionRoot();

    const refs: SourceRef[] = [];
    const listener = new FrendsExpressionParserListener();
    listener.enterSourceRef = (ctx: SourceRefContext) => {
        const token = ctx.SOURCE_REF().symbol;
        const tokenStart = token.start;
        const { path, pathEnd, tokenEnd } = parseSourceRef(expression, tokenStart);

        refs.push({ path, start: tokenStart, pathEnd, tokenEnd });
    };
    ParseTreeWalker.DEFAULT.walk(listener, tree);
    return refs;
}

export function extractSourcePaths(expression: string, mode: VdmExpressionMode = "text"): string[] {
    if (!expression || !expression.toLowerCase().includes(SOURCE_KEYWORD)) {
        return [];
    }
    const refs = collectSourceRefs(expression, mode);
    const paths: string[] = [];
    for (const ref of refs) {
        if (!paths.includes(ref.path)) {
            paths.push(ref.path);
        }
    }
    return paths;
}

// Extracts environment variable keys from an expression.
// E.g. "{{#env.Group1.Var1}}" -> ["Group1.Var1"]
export function extractEnvPaths(expression: string, mode: VdmExpressionMode): string[] {
    if (!expression || !expression.toLowerCase().includes(ENV_KEYWORD)) {
        return [];
    }
    const language = mode === "csharp" ? "csharp" : "handlebars";
    const parser = createParser(expression, language);
    const tree = mode === "csharp"
        ? parser.csExpressionRoot()
        : parser.textExpressionRoot();
    const paths: string[] = [];
    const listener = new FrendsExpressionParserListener();
    listener.enterEnvRef = (ctx: EnvRefContext) => {
        // ENV_REF_START text is like "#env.GroupName", ENV_VAR_VALUE is like ".VarName"
        const refStart = ctx.ENV_REF_START().getText();
        const group = refStart.substring(ENV_KEYWORD.length + 1); // strip "#env."
        const varValue = ctx.ENV_VAR_VALUE();
        const path = varValue ? group + varValue.getText() : group; // varValue includes leading dot
        if (!paths.includes(path)) {
            paths.push(path);
        }
    };
    ParseTreeWalker.DEFAULT.walk(listener, tree);
    return paths;
}

export function removeSourcePath(expression: string, path: string, mode: VdmExpressionMode = "text"): string {
    if (mode === "csharp") {
        const refs = collectSourceRefs(expression, mode);
        // Build result by removing matched refs from end to start (so positions stay valid)
        let result = expression;
        for (let i = refs.length - 1; i >= 0; i--) {
            if (refs[i].path === path) {
                result = result.substring(0, refs[i].start) + result.substring(refs[i].pathEnd);
            }
        }
        // Clean up dangling operators left behind (e.g. " + #source.b" → "#source.b", "#source.a + " → "#source.a")
        result = result.replace(/^\s*(\+|&&)\s*/, "").replace(/\s*(\+|&&)\s*$/, "");
        return result;
    }

    const escapedPath = path.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const escaped = path ? `\\.${escapedPath}` : "";
    const pattern = new RegExp(`\\{\\{#source${escaped}\\}\\}`, "gi");
    return expression.replace(pattern, "");
}

export function appendSourcePath(expression: string, path: string, mode: VdmExpressionMode = "text", targetType?: string): string {
    const token = formatSourceToken(path, mode);
    if (!expression || expression.trim().length === 0) {
        return token;
    }
    if (mode === "csharp") {
        const operator = targetType === "boolean" ? " && " : " + ";
        return `${expression}${operator}${token}`;
    }
    return `${expression}${token}`;
}

// Checks whether the expression contains a #source reference outside of {{}} handlebars (text mode only).
export function hasBareReference(expression: string): boolean {
    if (!expression) {
        return false;
    }
    const withoutHandlebars = expression.replace(/\{\{[^}]*\}\}/g, "");
    return /#source\b/i.test(withoutHandlebars);
}

// Whether the expression contains handlebars syntax ({{#...}}), invalid in csharp mode - the csharp-side mirror of hasBareReference.
export function hasHandlebarsReference(expression: string): boolean {
    if (!expression) {
        return false;
    }
    return /\{\{\s*#/.test(expression);
}

// Converts a direct text mapping to its csharp form by matching the value against what the direct-mapping
// generator would produce: a lone "{{#source.x}}" / "{{#env.x}}" becomes "#source.x" / "#env.x". Returns
// null for anything else (composite text, other reference types, handlebars helpers), leaving it untouched
// so it stays clearly broken for the user to fix.
export function directTextMappingToCsharp(value: string): string | null {
    const trimmed = value.trim();

    const sourcePaths = extractSourcePaths(trimmed, "text");
    if (sourcePaths.length === 1 && trimmed === formatSourceToken(sourcePaths[0], "text")) {
        return formatSourceToken(sourcePaths[0], "csharp");
    }

    const envPaths = extractEnvPaths(trimmed, "text");
    if (envPaths.length === 1 && trimmed === `{{#env.${envPaths[0]}}}`) {
        return `#env.${envPaths[0]}`;
    }

    return null;
}

// Resolves the editor mode when opening a mapping for edit. Text mode is only valid for a string
// target; for any non-string target the mode must be csharp (the Text/Expression toggle is hidden
// there). String targets keep the stored choice, since both modes are valid.
export function resolveEditorMode(
    initialMode: VdmExpressionMode | undefined,
    targetType: string,
): VdmExpressionMode {
    return targetType === "string" ? (initialMode ?? "text") : "csharp";
}
